import { ReactElement, useEffect, useState } from "react";
import { T } from "react-translator-component";
import { Avatar, Box, Button, List, ListItem, ListItemText, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { colors } from "../../../common/colors";
import { useAppDispatch } from "../../../redux/store";
import { convertUserImage, displayUserNickname } from "../../../util/utils";
import { setErrorObj } from "../../redux/errorReducer";
import { downloadCsv } from "../../util/util";
import CopyText from "../CopyText";
import Loading from "../Loading";

interface Props {
  member_group: any;
  member_group_users: any[];
}

type User = {
  image: string;
  nickname: string;
  external_id: string;
  role: string;
}

export default function MemberGroupUsers({ member_group, member_group_users }: Props): ReactElement {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (!member_group) return;
    setMemberGroupUsers(member_group_users);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member_group]);

  const setMemberGroupUsers = (member_group_users: any[]) => {
    const _users: User[] = [];

    const leader = {
      image: member_group.owner.image,
      nickname: member_group.owner.nickname ?? `${T("no_nickname")}`,
      external_id: member_group.owner.external_id,
      role: `${T("mng_member_groups_users_leader")}`
    }
    _users.push(leader);

    const subleaders = member_group_users.filter((user: any) => user.group_role === "sub-leader");
    subleaders.forEach((user: any) => _users.push({
      image: user.image,
      nickname: user.nickname,
      external_id: user.external_id,
      role: `${T("mng_member_groups_users_subleader")}`
    }));

    const finances = member_group_users.filter((user: any) => user.group_role === "finance");
    finances.forEach((user: any) => _users.push({
      image: user.image,
      nickname: user.nickname,
      external_id: user.external_id,
      role: `${T("mng_member_groups_users_finance")}`
    }));

    const members = member_group_users.filter((user: any) => !["sub-leader", "finance"].includes(user.group_role));
    members.forEach((user: any) => _users.push({
      image: user.image,
      nickname: user.nickname,
      external_id: user.external_id,
      role: ""
    }));

    setUsers(_users);
  }

  /* イベント */
  const download = () => {
    setIsLoading(true);
    try {
      downloadCsv({
        filename: `${member_group.name.slice(0, 100).replace(/[\\/:*?"<>|]/g, "_")}.csv`,
        headers: ['external_id', 'nickname'],
        fieldNames: ['external_id', 'nickname'],
        data: users.map((user: any) => ({ external_id: user.external_id, nickname: displayUserNickname(user.nickname) }))
      });
    } catch(e) {
      dispatch(setErrorObj(`${T("mng_member_groups_users_fail_output_csv")}`));
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box sx={styles.container}>
      <Loading visible={isLoading}/>

      <Box sx={styles.header.container}>
        {/* ユーザー数がゼロならユーザー取得でエラーが発生しているため、人数を表示しない。 */}
        <Typography sx={styles.header.member_count}>{T("mng_member_groups_users_count")} : {users.length === 0 ? '' : users.length}</Typography>
        <Button disabled={users.length === 0} onClick={download} startIcon={<FileDownloadOutlinedIcon />} variant="text" sx={styles.header.csv}>
          {T("mng_member_groups_users_csv")}
        </Button>
      </Box>

      <List>
        {users.map((user: any, index: number) => (
          <ListItem key={index} sx={styles.list.container}>
            <Avatar src={convertUserImage(user?.image)} sx={styles.list.image} />
            <ListItemText sx={styles.list.nickname}>
              <CopyText text={user.nickname} allowSelectingText={true} />
            </ListItemText>
            <ListItemText sx={styles.list.externalId}>
              <CopyText text={user.external_id} allowSelectingText={true} />
            </ListItemText>
            <ListItemText sx={styles.list.role}>
              {user.role}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const styles = {
  container: {
    maxHeight: "calc(100vh*0.9*0.7 - 90px)",
    overflowY: "auto" as const,
  },
  header: {
    container: {
      marginX: 3,
      marginY: 2,
      display: "flex",
      justifyContent: "space-between",
    },
    member_count: {
      display: "flex",
      alignItems: "center",
      marginRight: 3
    },
    csv: {
      color: "inherit"
    }
  },
  list: {
    container: {
      borderBottomStyle: "solid" as const,
      borderBottomColor: colors.farm_gray100,
      borderBottomWidth: 1
    },
    image: {
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      objectFit: "cover",
      marginRight: 1
    },
    nickname: {
      width: "100%",
      marginRight: 2
    },
    externalId: {
      width: "25%",
      minWidth: "9em"
    },
    role: {
      width: "25%",
      minWidth: "7em"
    }
  }
}
